import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Award, Trophy, Download, ExternalLink, Calendar, Clock,
  FileText, Loader2, CheckCircle, Star, ChevronRight,
  User, Share2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MyConstanciasPage() {
  const [constancias, setConstancias] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('constancias');
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [constanciasRes, recognitionsRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/api/recognition/my-constancias`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/recognition/my-recognitions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/recognition/my-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setConstancias(constanciasRes.data.constancias || []);
      setRecognitions(recognitionsRes.data.recognitions || []);
      setProgress(progressRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const contentTypeNames = {
    curso: 'Curso',
    webinar: 'Webinar',
    masterclass: 'Master Class',
    diplomado: 'Diplomado',
    taller: 'Taller',
    ponencia: 'Ponencia',
    platica: 'Plática'
  };

  const handleDownload = (downloadUrl) => {
    window.open(`${API_URL}${downloadUrl}`, '_blank');
  };

  const handleShare = (code, type) => {
    const url = `${window.location.origin}/verificar-${type}/${code}`;
    navigator.clipboard.writeText(url);
    alert('Enlace copiado al portapapeles');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">Mis Constancias y Reconocimientos</h1>
              <p className="text-indigo-200">
                Tu historial de logros en UPIM Academia
              </p>
            </div>
            {progress?.affiliation_number && (
              <div className="bg-white/10 backdrop-blur rounded-lg px-6 py-4">
                <p className="text-xs text-indigo-200 mb-1">Número de Afiliación</p>
                <p className="text-xl font-bold tracking-wider">{progress.affiliation_number}</p>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <FileText className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
              <p className="text-2xl font-bold">{constancias.length}</p>
              <p className="text-sm text-indigo-200">Constancias</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <Trophy className="w-6 h-6 mx-auto mb-2 text-amber-400" />
              <p className="text-2xl font-bold">{recognitions.length}</p>
              <p className="text-sm text-indigo-200">Reconocimientos</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-400" />
              <p className="text-2xl font-bold">
                {constancias.filter(c => c.quiz_passed).length}
              </p>
              <p className="text-sm text-indigo-200">Exámenes aprobados</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
              <Star className="w-6 h-6 mx-auto mb-2 text-yellow-400" />
              <p className="text-2xl font-bold">{progress?.programs?.filter(p => p.is_completed).length || 0}</p>
              <p className="text-sm text-indigo-200">Programas completados</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Progress towards recognitions */}
        {progress?.programs && progress.programs.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Progreso hacia Reconocimientos
            </h2>
            <div className="space-y-4">
              {progress.programs.map(program => (
                <div key={program.id} className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${program.badge_color}20` }}
                      >
                        <Trophy className="w-5 h-5" style={{ color: program.badge_color }} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{program.name}</p>
                        <p className="text-sm text-gray-500">
                          {program.is_completed ? (
                            <span className="text-green-600 font-medium">¡Completado!</span>
                          ) : (
                            `${progress.total_constancias} de ${program.required_constancias} constancias`
                          )}
                        </p>
                      </div>
                    </div>
                    <span className="text-lg font-bold" style={{ color: program.badge_color }}>
                      {Math.round(program.progress_percent)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${program.progress_percent}%`,
                        backgroundColor: program.badge_color
                      }}
                    />
                  </div>
                  {!program.is_completed && program.remaining > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Te faltan {program.remaining} constancia{program.remaining > 1 ? 's' : ''} para obtener este reconocimiento
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('constancias')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'constancias'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Constancias ({constancias.length})
            </span>
          </button>
          <button
            onClick={() => setActiveTab('recognitions')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'recognitions'
                ? 'bg-amber-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Reconocimientos ({recognitions.length})
            </span>
          </button>
        </div>

        {/* Constancias List */}
        {activeTab === 'constancias' && (
          <div className="space-y-4">
            {constancias.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No tienes constancias aún</h3>
                <p className="text-gray-500 mb-6">
                  Completa cursos, talleres y webinars para obtener tus constancias
                </p>
                <Link
                  to="/masterclasses"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
                >
                  Explorar Academia
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              constancias.map(constancia => (
                <div
                  key={constancia.id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                >
                  <div className="flex items-stretch">
                    <div className="w-2 bg-indigo-600" />
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                              {contentTypeNames[constancia.content_type] || constancia.content_type}
                            </span>
                            {constancia.quiz_passed && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Examen aprobado
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {constancia.content_title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            {constancia.speaker_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {constancia.speaker_name}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(constancia.content_date)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleShare(constancia.verification_code, 'constancia')}
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="Compartir"
                          >
                            <Share2 className="w-5 h-5" />
                          </button>
                          <Link
                            to={`/verificar-constancia/${constancia.verification_code}`}
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="Ver verificación"
                          >
                            <Eye className="w-5 h-5" />
                          </Link>
                          <button
                            onClick={() => handleDownload(constancia.download_url)}
                            className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            title="Descargar PDF"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-xs text-gray-400">
                          Código: <span className="font-mono">{constancia.verification_code}</span>
                        </span>
                        <span className="text-xs text-gray-400">
                          Emitida: {formatDate(constancia.issued_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Recognitions List */}
        {activeTab === 'recognitions' && (
          <div className="space-y-4">
            {recognitions.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No tienes reconocimientos aún</h3>
                <p className="text-gray-500 mb-6">
                  Acumula constancias para obtener reconocimientos especiales
                </p>
                {progress?.programs && progress.programs.length > 0 && (
                  <p className="text-sm text-indigo-600">
                    Estás a {progress.programs[0]?.remaining || 0} constancias del primer reconocimiento
                  </p>
                )}
              </div>
            ) : (
              recognitions.map(recognition => (
                <div
                  key={recognition.id}
                  className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl shadow-sm overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
                          <Trophy className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <p className="text-xs text-amber-600 font-medium uppercase tracking-wider mb-1">
                            Reconocimiento
                          </p>
                          <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {recognition.program_name}
                          </h3>
                          <p className="text-gray-600">{recognition.recognition_title}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {recognition.total_constancias} constancias
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(recognition.issued_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleShare(recognition.verification_code, 'reconocimiento')}
                          className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg"
                          title="Compartir"
                        >
                          <Share2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownload(recognition.download_url)}
                          className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                          title="Descargar PDF"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
